import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/useAuth';

export interface BrowseCategory {
  id: string;
  name: string;
  parent_id: string | null;
  company_id: string | null;
  display_order: number;
  depth: number;
  is_global: boolean;
}

export interface BrowseCategoryNode extends BrowseCategory {
  children: BrowseCategoryNode[];
}

const QUERY_KEY = ['browse_categories'];
const STALE_TIME = 10 * 60 * 1000;

export function useBrowseCategories() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const companyId = profile?.company_id ?? null;

  const query = useQuery({
    queryKey: [...QUERY_KEY, companyId],
    queryFn: async (): Promise<BrowseCategory[]> => {
      if (!companyId) return [];
      const { data, error } = await (supabase.rpc as any)(
        'get_browse_category_tree',
        { p_company_id: companyId }
      );
      if (error) throw error;
      return (data as BrowseCategory[]) ?? [];
    },
    enabled: !!companyId,
    staleTime: STALE_TIME,
  });

  useEffect(() => {
    const channel = supabase
      .channel('browse_categories_changes')
      .on(
        'postgres_changes' as any,
        { event: '*', schema: 'public', table: 'browse_categories' },
        () => {
          queryClient.invalidateQueries({
            queryKey: [...QUERY_KEY, companyId]
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  const buildTree = (
    flat: BrowseCategory[],
    parentId: string | null = null
  ): BrowseCategoryNode[] => {
    return flat
      .filter(c => c.parent_id === parentId)
      .sort((a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name))
      .map(c => ({ ...c, children: buildTree(flat, c.id) }));
  };

  const buildPath = (
    categoryId: string | null,
    flat: BrowseCategory[]
  ): string => {
    if (!categoryId) return '';
    const parts: string[] = [];
    let current = flat.find(c => c.id === categoryId);
    while (current) {
      parts.unshift(current.name);
      current = current.parent_id
        ? flat.find(c => c.id === current!.parent_id)
        : undefined;
    }
    return parts.join(' > ');
  };

  const flat = query.data ?? [];
  const tree = useMemo(() => buildTree(flat), [flat]);

  return {
    flat,
    tree,
    isLoading: query.isLoading,
    error: query.error,
    buildPath: (id: string | null) => buildPath(id, flat),
    getChildren: (parentId: string | null) =>
      flat
        .filter(c => c.parent_id === parentId)
        .sort((a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name)),
    hasChildren: (id: string) => flat.some(c => c.parent_id === id),
    findById: (id: string) => flat.find(c => c.id === id) ?? null,
  };
}
